import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Modal,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Button } from 'reactstrap';

import colors from 'src/styles/equalution/colors';
import LogoImage from 'src/assets/images/logo-new.svg';
import CoachingIcon from 'src/assets/icons/coaching.svg';

interface ChallengeNutritionUpsellModalProps {
  open: boolean;
  price: number;
  isSubmitting?: boolean;
  errorMessage?: string;
  onDecline: () => void;
  onConfirmPurchase: () => Promise<void> | void;
}

const featureItems = [
  'Ongoing accountability and personalised feedback to keep you consistent.',
  "Weekly check-in feedback, tailored education, and expert oversight ensure progress doesn't stall when life gets busy.",
  'Cancel anytime.',
];

const ChallengeNutritionUpsellModal: FC<ChallengeNutritionUpsellModalProps> = ({
  open,
  price,
  isSubmitting = false,
  errorMessage = '',
  onDecline,
  onConfirmPurchase,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showConfirmationSheet, setShowConfirmationSheet] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowConfirmationSheet(false);
    }
  }, [open]);

  const formattedPrice = useMemo(() => {
    if (Number.isNaN(price)) return '0';
    return Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2);
  }, [price]);

  const handleClose = () => {
    if (!isSubmitting) {
      onDecline();
    }
  };

  const handleSheetClose = () => {
    if (!isSubmitting) {
      setShowConfirmationSheet(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
          backgroundColor: colors.OFF_WHITE,
          boxSizing: 'border-box',
        }}>
        <Stack
          sx={{
            minHeight: '100%',
            width: '100%',
            maxWidth: '480px',
            margin: '0 auto',
            padding: isMobile ? '28px 20px 32px' : '32px 28px 40px',
            position: 'relative',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
          }}>
          <Box>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between">
              <img src={LogoImage} alt="equ" width={70} />
              <IconButton onClick={handleClose} sx={{ p: 0 }}>
                <CloseIcon
                  sx={{ color: colors.OFF_BLACK, fontSize: isMobile ? 34 : 40 }}
                />
              </IconButton>
            </Stack>

            <Stack alignItems="center" mt={isMobile ? 5 : 7}>
              <Box
                sx={{
                  width: 65,
                  height: 65,
                  borderRadius: '50%',
                  backgroundColor: colors.SALAD,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <CheckIcon sx={{ color: colors.WHITE, fontSize: 45 }} />
              </Box>

              <Typography
                sx={{
                  fontSize: '28px',
                  lineHeight: 1.1,
                  fontWeight: 600,
                  color: colors.OFF_BLACK,
                  textAlign: 'center',
                  mt: 4,
                }}>
                Payment confirmed
              </Typography>

              <Box
                sx={{
                  width: '100%',
                  mt: 4,
                  padding: isMobile ? '18px 20px' : '20px 24px',
                  border: `2px solid ${colors.SALAD}`,
                  borderRadius: '28px',
                  backgroundColor: colors.WHITE,
                  textAlign: 'center',
                  position: 'relative',
                  boxSizing: 'border-box',
                }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: 1.35,
                    color: colors.OFF_BLACK,
                  }}>
                  You&apos;re 95% more likely to hit your goals with a personal
                  nutritionist
                </Typography>
                <Box
                  sx={{
                    position: 'absolute',
                    right: '-10px',
                    bottom: '-26px',
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    backgroundColor: '#B8F0D8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <img
                    src={CoachingIcon}
                    alt="Nutrition coach"
                    width={30}
                    height={30}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  mt: -6,
                  padding: '24px',
                  paddingTop: '60px',
                  borderRadius: '32px',
                  backgroundColor: colors.CREAM_HALF,
                  boxSizing: 'border-box',
                }}>
                <Typography
                  sx={{
                    fontSize: isMobile ? '20px' : '24px',
                    lineHeight: 1.2,
                    fontWeight: 700,
                    color: colors.SALAD,
                    textAlign: 'center',
                    mb: 3,
                  }}>
                  Add 1:1 nutrition coaching for just ${formattedPrice}/month
                </Typography>

                <Stack gap={4}>
                  {featureItems.map(feature => (
                    <Stack
                      direction="row"
                      gap={2}
                      alignItems="flex-start"
                      key={feature}>
                      <CheckIcon
                        sx={{
                          color: colors.SALAD,
                          fontSize: 24,
                          mt: '2px',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '15px',
                          lineHeight: 1.45,
                          color: colors.OFF_BLACK,
                        }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Stack gap={2} mt={5}>
            <Button
              onClick={() => setShowConfirmationSheet(true)}
              disabled={isSubmitting}>
              Add coaching - ${formattedPrice} billed today
            </Button>
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                border: `2px solid ${colors.SALAD}`,
                backgroundColor: 'transparent',
                color: colors.SALAD,
              }}>
              No thanks, I&apos;ll go it alone
            </Button>
          </Stack>
        </Stack>

        {showConfirmationSheet && (
          <>
            <Box
              onClick={handleSheetClose}
              sx={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(16, 24, 32, 0.7)',
                zIndex: 1300,
              }}
            />
            <Box
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                height: isMobile ? '80vh' : '70vh',
                backgroundColor: colors.OFF_WHITE,
                borderTopLeftRadius: '32px',
                borderTopRightRadius: '32px',
                padding: '24px 20px 32px',
                zIndex: 1301,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}>
              <Box
                sx={{
                  width: 72,
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: `${colors.OFF_BLACK}20`,
                  margin: '0 auto 28px',
                }}
              />

              <Stack
                sx={{
                  width: '100%',
                  maxWidth: '480px',
                  margin: '0 auto',
                  flex: 1,
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                }}>
                <Typography
                  sx={{
                    fontSize: '24px',
                    lineHeight: 1.15,
                    fontWeight: 700,
                    color: colors.OFF_BLACK,
                    mb: 3,
                  }}>
                  Confirm Coaching Add-On
                </Typography>

                <Box
                  sx={{
                    backgroundColor: colors.WHITE,
                    borderRadius: '28px',
                    padding: '24px 20px',
                  }}>
                  <Stack gap={2.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={2}>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          lineHeight: 1.3,
                          fontWeight: 500,
                          color: colors.OFF_BLACK,
                        }}>
                        1:1 Nutrition{isMobile ? <br /> : ' '}Coaching
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          lineHeight: 1.3,
                          fontWeight: 700,
                          color: colors.SALAD,
                          textAlign: 'right',
                        }}>
                        ${formattedPrice}/month
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        borderTop: `1px solid ${colors.OFF_BLACK}20`,
                      }}
                    />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-end"
                      gap={2}>
                      <Typography
                        sx={{
                          fontSize: '20px',
                          lineHeight: 1,
                          fontWeight: 700,
                          color: colors.OFF_BLACK,
                        }}>
                        Total
                      </Typography>
                      <Box textAlign="right">
                        <Typography
                          sx={{
                            fontSize: '20px',
                            lineHeight: 1,
                            fontWeight: 700,
                            color: colors.SALAD,
                          }}>
                          ${formattedPrice}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            lineHeight: 1.2,
                            color: colors.OFF_BLACK,
                          }}>
                          billed today
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Box>

                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: 1.45,
                    color: colors.OFF_BLACK,
                    mt: 3,
                  }}>
                  Your 1:1 Nutrition Coaching subscription automatically renews
                  monthly at ${formattedPrice}/month, unless cancelled.
                </Typography>

                {errorMessage && (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: 1.4,
                      color: colors.ERROR_RED,
                      mt: 1,
                    }}>
                    {errorMessage}
                  </Typography>
                )}

                <Stack direction="row" gap={2} mt={4}>
                  <Button
                    onClick={onConfirmPurchase}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: 0,
                    }}>
                    {isSubmitting ? 'Processing...' : 'Confirm payment'}
                  </Button>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    style={{
                      borderRadius: 999,
                      backgroundColor: colors.CREAM_HALF,
                      color: `${colors.BLUEBERRY}CC`,
                      fontSize: 16,
                      border: 'none',
                    }}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ChallengeNutritionUpsellModal;
